/**
 * envBackground.ts — Tinte de color de fondo full-screen según el ambiente.
 *
 * Objetivo: dar identidad visual para no confundir en qué ambiente se está
 * (localhost / staging / producción).
 *
 * - `getEnvironment()` es una función PURA basada en `window.location.hostname`
 *   (determinística, SIN storage). Cubre el 90% del pedido: el color por
 *   ambiente sale solo del hostname.
 * - El override manual (elegir otro color desde el Debug Drawer) SÍ persiste,
 *   y TODO el acceso a localStorage está encapsulado acá (getOverride /
 *   setOverride / clearOverride) para que sea trivial cambiar la estrategia
 *   después (ej. no persistir, o mover a Firestore).
 *
 * ⚠️ NOTA localStorage: el override usa localStorage con la key
 * `pulpou.debug.envBgOverride`. Es una preferencia de UI a nivel dispositivo
 * (dev tool, no dato sensible). Pendiente de aprobación explícita de Gabriel
 * (regla del proyecto: localStorage requiere OK). El feature funciona igual
 * SIN override: el default por ambiente no toca storage.
 */

export type Environment = 'local' | 'staging' | 'production';

/**
 * Detecta el ambiente desde el hostname. Determinístico, sin side-effects.
 * - localhost / 127.0.0.1 / [::1] → 'local'
 * - ptstaging.pulpouplatform.com → 'staging'
 * - resto (dominio de prod) → 'production'
 */
export function getEnvironment(): Environment {
  if (typeof window === 'undefined') return 'production';
  const host = (window.location.hostname || '').toLowerCase();

  if (host === 'localhost' || host === '127.0.0.1' || host === '::1' || host === '[::1]') {
    return 'local';
  }
  if (host.startsWith('ptstaging.')) {
    return 'staging';
  }
  return 'production';
}

/**
 * Opción de color de fondo. `bgClass` es una clase Tailwind (o vacía en el
 * caso "sin tinte"). Los tonos son pasteles reusados de la matriz de
 * tendencias (`CategoryPriceMatrix.tsx`: bg-*-50/100, tonos suaves).
 */
export interface BgColorOption {
  id: string;
  label: string;
  /** Clase Tailwind de fondo. '' = sin tinte (fondo actual). */
  bgClass: string;
  /** Muestra de color para el selector (hex del tono Tailwind equivalente). */
  swatch: string;
}

/**
 * Paleta reusada de la matriz de tendencias (CategoryPriceMatrix.tsx).
 * Solo tonos suaves (50/100). Incluye "Producción (sin tinte)" como default
 * de prod para que prod se vea "normal".
 */
export const BG_COLOR_OPTIONS: BgColorOption[] = [
  { id: 'none', label: 'Sin tinte (producción)', bgClass: 'bg-gray-50', swatch: '#f9fafb' },
  { id: 'green', label: 'Verde suave', bgClass: 'bg-green-50', swatch: '#f0fdf4' },
  { id: 'orange', label: 'Naranja suave', bgClass: 'bg-orange-50', swatch: '#fff7ed' },
  { id: 'blue', label: 'Azul suave', bgClass: 'bg-blue-50', swatch: '#eff6ff' },
  { id: 'green-100', label: 'Verde medio', bgClass: 'bg-green-100', swatch: '#dcfce7' },
  { id: 'orange-100', label: 'Naranja medio', bgClass: 'bg-orange-100', swatch: '#ffedd5' },
  { id: 'blue-100', label: 'Azul medio', bgClass: 'bg-blue-100', swatch: '#dbeafe' },
];

/**
 * Default por ambiente (derivado del hostname, sin storage):
 * - production → 'none' (fondo actual, se ve normal)
 * - staging    → naranja suave (pastel matriz)
 * - local      → azul suave (pastel matriz)
 */
const DEFAULT_BY_ENV: Record<Environment, string> = {
  production: 'none',
  staging: 'orange',
  local: 'blue',
};

export function getDefaultColorIdForEnv(env: Environment = getEnvironment()): string {
  return DEFAULT_BY_ENV[env] ?? 'none';
}

// ===== Persistencia del override (encapsulada en este módulo) =====
// Único punto de acceso a localStorage para este feature. Cambiar la
// estrategia (no persistir / Firestore) toca SOLO estas tres funciones.

const OVERRIDE_KEY = 'pulpou.debug.envBgOverride';

/** Lee el override manual del usuario, o null si no hay. */
export function getOverrideColorId(): string | null {
  try {
    const v = window.localStorage.getItem(OVERRIDE_KEY);
    if (!v) return null;
    // Validar contra la paleta conocida (evita valores basura).
    return BG_COLOR_OPTIONS.some((o) => o.id === v) ? v : null;
  } catch {
    return null;
  }
}

/** Guarda el override manual. */
export function setOverrideColorId(id: string): void {
  try {
    window.localStorage.setItem(OVERRIDE_KEY, id);
  } catch {
    // ignore (storage lleno / bloqueado)
  }
}

/** Borra el override → vuelve al default del ambiente. */
export function clearOverrideColorId(): void {
  try {
    window.localStorage.removeItem(OVERRIDE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Resuelve el id de color efectivo: override manual si existe, si no el
 * default del ambiente.
 */
export function resolveActiveColorId(env: Environment = getEnvironment()): string {
  return getOverrideColorId() ?? getDefaultColorIdForEnv(env);
}

/** Devuelve la clase Tailwind de fondo para un id de color dado. */
export function getBgClassForColorId(id: string): string {
  const opt = BG_COLOR_OPTIONS.find((o) => o.id === id);
  return opt ? opt.bgClass : 'bg-gray-50';
}

// ===== Notificación de cambios (para re-render sin acoplar contexts) =====
// El Debug Drawer (superadmin) puede cambiar el override; App.tsx aplica el
// bg full-width y debe re-renderizar. Como App.tsx NO está dentro del
// DebugContext (que es superadmin-only y su alcance es distinto), usamos un
// mini pub/sub en memoria en vez de acoplar ambos contextos.

type Listener = () => void;
const listeners = new Set<Listener>();

/** Suscribe un listener a cambios del color de fondo. Devuelve unsubscribe. */
export function subscribeBgChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** Notifica a los suscriptores que el color cambió (llamar tras set/clear). */
export function notifyBgChange(): void {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      // ignore
    }
  });
}
